import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { NavigationGlyph, SetupAppearanceSelector, type NavigationLink } from '@traceio-org/ui';
import { APPEARANCE_LABELS, usePopupDismiss, type AppearancePreference } from '@traceio-org/auth-ui';

/** The id and href of NavigationShell's appearanceItem; the href only matters for a modified click (a new tab). */
export const APPEARANCE_ITEM_ID = 'appearance';
const APPEARANCE_HREF = '#appearance';

/** NavigationShell's footer entry: "Appearance" with the current choice as its trailing text. */
export function appearanceItem(preference: AppearancePreference): NavigationLink {
  return {
    id: APPEARANCE_ITEM_ID,
    label: 'Appearance',
    href: APPEARANCE_HREF,
    icon: <NavigationGlyph name="Sun" />,
    trailingLabel: APPEARANCE_LABELS[preference],
  };
}

/**
 * The sidebar row NavigationShell renders for appearanceItem. trace-ui renders it as a link and offers no ref or
 * popup props, so the menu finds it by its href: to place itself next to it, to tell screen readers that it opens a
 * popup and whether it is open, and to give focus back to it. Ask trace-ui for a button-shaped appearanceItem with
 * popup semantics; until then this is the one place that reaches into the shell's markup.
 */
function appearanceTrigger(): HTMLAnchorElement | null {
  const rows = document.querySelectorAll<HTMLAnchorElement>(`a.trace-nav-link[href="${APPEARANCE_HREF}"]`);
  // The sidebar exists twice on a phone (the rail and the slide-out): the visible one (with a layout box) first.
  for (const row of rows) if (row.getClientRects().length > 0) return row;
  return rows[0] ?? null;
}

/**
 * Keeps every Appearance row's popup attributes in step with the menu (aria-haspopup always, aria-expanded while
 * open), including rows mounted later: the phone slide-out sidebar renders its own copy of the row when it opens, in
 * a portal at the end of <body>, outside the shell's subtree. So the observer watches <body>'s children (subtree),
 * re-applies only when a row appears, and is disconnected when the menu state changes or Trace Admin unmounts.
 */
export function useAppearanceTriggerState(open: boolean): void {
  useEffect(() => {
    const apply = () => {
      for (const row of document.querySelectorAll<HTMLAnchorElement>(
        `a.trace-nav-link[href="${APPEARANCE_HREF}"]`,
      )) {
        if (row.getAttribute('aria-haspopup') !== 'dialog') row.setAttribute('aria-haspopup', 'dialog');
        if (row.getAttribute('aria-expanded') !== String(open))
          row.setAttribute('aria-expanded', String(open));
      }
    };
    apply();
    // Attribute writes are not observed (childList only), so applying never re-triggers the observer.
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open]);
}

const GAP = 6;
const EDGE = 12;

/**
 * The appearance menu: the three SetupAppearanceSelector choices in trace-ui's workspace-menu surface, opened from the
 * sidebar's Appearance row. A non-modal dialog: focus moves to the checked choice, arrow keys move between the choices
 * (native radios, each applies at once so the page previews it), and Escape, a click outside or Tab out of it close it
 * and give focus back to the row. Portaled into a trace-nav-portal element, as trace-ui's own menus, so it reads the
 * shell's surface tokens and sits above the page.
 */
export function AppearanceMenu({
  preference,
  shared,
  onChange,
  onClose,
}: {
  preference: AppearancePreference;
  /** True when the choice is stored on the parent domain the account hub shares (useAppearance `shared`). */
  shared: boolean;
  onChange: (next: AppearancePreference) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Starts at the fallback place (never hidden: the focus move into it needs it visible) and is moved next to the
  // row before the first paint.
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', left: EDGE, bottom: EDGE });

  // Above the row, left-aligned with it, kept on screen; bottom-left when the row is not visible (a phone's
  // slide-out closes on selection, and the shell's search can open the entry with no row on screen).
  useLayoutEffect(() => {
    const place = () => {
      const row = appearanceTrigger();
      const width = panel.current?.offsetWidth ?? 280;
      if (!row || row.getClientRects().length === 0) {
        setStyle({ position: 'fixed', left: EDGE, bottom: EDGE });
        return;
      }
      const rect = row.getBoundingClientRect();
      const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE));
      setStyle({ position: 'fixed', left, bottom: Math.max(EDGE, window.innerHeight - rect.top + GAP) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, []);

  usePopupDismiss({ panel, trigger: appearanceTrigger, onDismiss: onClose });

  return createPortal(
    <div className="trace-nav-portal">
      <div
        ref={panel}
        className="trace-workspace-menu admin-appearance-menu"
        role="dialog"
        aria-label="Appearance"
        style={style}
      >
        {/* trace-ui's SetupAppearance is the same three words as AppearancePreference. */}
        <SetupAppearanceSelector value={preference} onChange={onChange} />
        <p className="admin-appearance-menu__hint">
          System follows your device.
          {/* Only true when the cookie is on the shared parent domain, never for a host-only fallback. */}
          {shared ? ' The account hub uses the same choice.' : null}
        </p>
      </div>
    </div>,
    document.body,
  );
}
